import { helpWhy } from '../../help/why';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { ExpOverdueService } from '../../services/garmentsCompliance.service';
import { ExpOverdueStatus, IOverdueCase, IOverdueRow, IOverdueWorklist } from '../../interfaces/garmentsCompliance.interface';
import { PERMISSIONS } from '../../constants/permissions';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { humanizeEnum } from '../../utils/format';

const STATUSES: ExpOverdueStatus[] = ['OPEN', 'BANK_FOLLOW_UP', 'EXTENSION_REQUESTED', 'EXTENSION_GRANTED', 'CLOSED'];
const NEXT: Record<ExpOverdueStatus, ExpOverdueStatus[]> = {
    OPEN: ['BANK_FOLLOW_UP', 'EXTENSION_REQUESTED'],
    BANK_FOLLOW_UP: ['EXTENSION_REQUESTED'],
    EXTENSION_REQUESTED: ['EXTENSION_GRANTED', 'BANK_FOLLOW_UP'],
    EXTENSION_GRANTED: ['BANK_FOLLOW_UP', 'EXTENSION_REQUESTED'],
    CLOSED: [],
};
const COLOR: Record<ExpOverdueStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
    OPEN: 'error', BANK_FOLLOW_UP: 'warning', EXTENSION_REQUESTED: 'info', EXTENSION_GRANTED: 'success', CLOSED: 'default',
};
const money = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Overdue export bills: EXP forms past their repatriation due date, with the bank follow-up and extension workflow. */
const OverdueExpBills: React.FC = () => {
    const toast = useToast();
    const { hasPermission } = useAuth();
    const [params] = useSearchParams();
    const institutionId = AuthService.getSelectedInstitution()!;
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const [status, setStatus] = useState<ExpOverdueStatus | ''>('');
    const [includeClosed, setIncludeClosed] = useState(false);
    const [list, setList] = useState<IOverdueWorklist | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<IOverdueCase | null>(null);
    const [target, setTarget] = useState<ExpOverdueStatus | ''>('');
    const [note, setNote] = useState('');
    const [reference, setReference] = useState('');
    const [extDate, setExtDate] = useState('');
    const [newDue, setNewDue] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    // A deep-linked EXP with no case yet (e.g. beyond the auto-open batch): offer to open one.
    const [orphanExpId, setOrphanExpId] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setList(await ExpOverdueService.worklist(institutionId, status, includeClosed));
        } catch (e) {
            toast.showApiError(e, 'Failed to load overdue export bills');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, status, includeClosed]);

    useEffect(() => { load(); }, [load]);

    const show = (c: IOverdueCase) => {
        setSelected(c);
        setTarget('');
        setNote('');
        setReference(c.extensionReference ?? '');
        setExtDate('');
        setNewDue('');
        setNotes(c.notes ?? '');
    };

    const openCase = useCallback(async (caseId: string) => {
        try {
            show(await ExpOverdueService.get(institutionId, caseId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the case');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    // Deep link from a reminder: ?expId= selects that EXP's case once the worklist is in.
    useEffect(() => {
        const expId = params.get('expId');
        if (!expId || !list) return;
        const row = list.rows.find(r => r.expId === expId);
        if (row) { setOrphanExpId(null); openCase(row.caseId); } else { setOrphanExpId(expId); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    const openForExp = async () => {
        if (!orphanExpId) return;
        try {
            show(await ExpOverdueService.open(institutionId, orphanExpId));
            setOrphanExpId(null);
            load();
        } catch (e) {
            toast.showApiError(e, 'Could not open a case for this EXP');
        }
    };

    const saveWhy = saving ? null
        : !target ? 'Pick the new status.'
        : null;

    const submit = async () => {
        if (!selected || !target) return;
        setSaving(true);
        try {
            const updated = await ExpOverdueService.transition(institutionId, selected.id, {
                toStatus: target, note: note || undefined, extensionReference: reference || undefined,
                extensionDate: extDate || undefined, newDueDate: newDue || undefined,
            });
            toast.showSuccess('Case updated');
            show(updated);
            load();
        } catch (e) {
            toast.showApiError(e, 'Failed to update the case');
        } finally {
            setSaving(false);
        }
    };

    const saveNotes = async () => {
        if (!selected) return;
        try {
            show(await ExpOverdueService.saveNotes(institutionId, selected.id, notes));
            toast.showSuccess('Notes saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save the notes');
        }
    };

    return (
        <Box data-help-page="commercial.overdue.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Overdue Export Bills</Typography>
            {list && <Alert severity="info" sx={{ mb: 2 }}>{list.verifyNote} Repatriation window: {list.repatriationWindowDays} days from the EXP date.</Alert>}
            <Box data-help-section="commercial.overdue.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <Box sx={{ flex: '1 1 220px', maxWidth: 300 }}>
                    <TextField data-help="commercial.overdue.status" select size="small" fullWidth label="Status" value={status} onChange={e => setStatus(e.target.value as ExpOverdueStatus | '')}>
                        <MenuItem value="">All open</MenuItem>
                        {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
                    </TextField>
                </Box>
                <FormControlLabel data-help="commercial.overdue.includeClosed" control={<Checkbox checked={includeClosed} onChange={e => setIncludeClosed(e.target.checked)} />} label="Include closed" />
            </Box>
            {orphanExpId && canManage && (
                <Alert severity="warning" sx={{ mb: 2 }} action={<Button data-help="commercial.overdue.openNew" color="inherit" size="small" onClick={openForExp}>Open case</Button>}>
                    This EXP has no overdue case yet. Open one if its repatriation due date has passed.
                </Alert>
            )}
            {loading && <CircularProgress size={24} />}
            {!loading && list && (
                <TableContainer component={Paper} data-testid="overdue-table" data-help-section="commercial.overdue.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.overdue.col.exp">EXP</TableCell><TableCell data-help="commercial.overdue.col.buyer">Buyer</TableCell><TableCell data-help="commercial.overdue.col.bank">Bank</TableCell>
                                <TableCell align="right" data-help="commercial.overdue.col.outstanding">Outstanding</TableCell><TableCell data-help="commercial.overdue.col.due">Due</TableCell>
                                <TableCell align="right" data-help="commercial.overdue.col.daysOverdue">Days overdue</TableCell><TableCell data-help="commercial.overdue.col.status">Status</TableCell><TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.rows.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary">No overdue export bills.</Typography></TableCell></TableRow>
                            )}
                            {list.rows.map((r: IOverdueRow) => (
                                <TableRow key={r.caseId} hover data-testid={`overdue-row-${r.expId}`}>
                                    <TableCell>{r.expNumber}</TableCell>
                                    <TableCell>{r.buyerName}</TableCell>
                                    <TableCell>{r.bankName}</TableCell>
                                    <TableCell align="right">{r.currencyCode} {money(r.outstanding)}</TableCell>
                                    <TableCell>{r.effectiveDueDate}{r.newDueDate ? ' (extended)' : ''}</TableCell>
                                    <TableCell align="right">{r.daysOverdue}</TableCell>
                                    <TableCell><Chip size="small" color={COLOR[r.status]} label={humanizeEnum(r.status)} /></TableCell>
                                    <TableCell><Button data-help="commercial.overdue.openCase" size="small" onClick={() => openCase(r.caseId)}>Open</Button></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md">
                {selected && (
                    <>
                        <DialogTitle>EXP {selected.expNumber ?? '(deleted EXP form)'} · {selected.buyerName ?? '—'}</DialogTitle>
                        <DialogContent dividers>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Status <Chip size="small" color={COLOR[selected.status]} label={humanizeEnum(selected.status)} /> · Outstanding {selected.currencyCode ?? ''} {selected.outstanding == null ? '—' : money(selected.outstanding)} ·
                                Original due {selected.originalDueDate ?? '—'} · Effective due {selected.effectiveDueDate ?? '—'}
                                {selected.closedDate ? ` · Closed ${selected.closedDate} (${humanizeEnum(selected.closeReason ?? '')})` : ''}
                            </Typography>
                            {selected.extensionReference && (
                                <Typography variant="body2" sx={{ mb: 1 }}>Extension ref {selected.extensionReference}
                                    {selected.newDueDate ? ` · new due date ${selected.newDueDate}` : ''}</Typography>
                            )}
                            {canManage && NEXT[selected.status].length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, my: 2 }}>
                                    <TextField data-help="commercial.overdue.moveTo" select size="small" label="Move to" value={target} onChange={e => setTarget(e.target.value as ExpOverdueStatus)}>
                                        {NEXT[selected.status].map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
                                    </TextField>
                                    {(target === 'EXTENSION_REQUESTED' || target === 'EXTENSION_GRANTED') && (
                                        <>
                                            <TextField data-help="commercial.overdue.extRef" size="small" label="Extension reference (bank letter no.)" value={reference} onChange={e => setReference(e.target.value)} />
                                            <TextField data-help="commercial.overdue.extDate" size="small" type="date" label="Letter / grant date" InputLabelProps={{ shrink: true }} value={extDate} onChange={e => setExtDate(e.target.value)} />
                                        </>
                                    )}
                                    {target === 'EXTENSION_GRANTED' && (
                                        <TextField data-help="commercial.overdue.newDue" size="small" type="date" label="New due date" InputLabelProps={{ shrink: true }} value={newDue} onChange={e => setNewDue(e.target.value)} />
                                    )}
                                    <TextField data-help="commercial.overdue.note" size="small" label="Note" value={note} onChange={e => setNote(e.target.value)} multiline minRows={2} />
                                    <Box><Button data-help="commercial.overdue.apply" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" size="small" disabled={!target || saving} onClick={submit}>Apply</Button></Box>
                                </Box>
                            )}
                            {canManage && selected.status !== 'CLOSED' && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                                    <TextField data-help="commercial.overdue.caseNotes" size="small" label="Case notes" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} sx={{ flex: 1 }} />
                                    <Button data-help="commercial.overdue.saveNotes" size="small" onClick={saveNotes}>Save notes</Button>
                                </Box>
                            )}
                            <Typography variant="subtitle2">History</Typography>
                            {selected.events.map(ev => (
                                <Typography key={ev.id} variant="body2" color="text.secondary">
                                    {ev.eventDate} · {humanizeEnum(ev.eventType)}{ev.toStatus ? ` → ${humanizeEnum(ev.toStatus)}` : ''}{ev.note ? ` · ${ev.note}` : ''}
                                </Typography>
                            ))}
                            <Box sx={{ mt: 2 }}>
                                <AttachmentsPanel entityType="EXP_OVERDUE_CASE" entityId={selected.id} canManage={canManage && selected.status !== 'CLOSED'} />
                            </Box>
                        </DialogContent>
                        <DialogActions><Button data-help="commercial.overdue.closeDialog" onClick={() => setSelected(null)}>Close</Button></DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default OverdueExpBills;
